/**
 * The backoffice's lists, the decisions on access requests, signing a
 * managed client's licence, whether Revia's GitHub App can act on Revent, and
 * connecting a client through it. One reconciler writes every cell, keyed on
 * the staff member signed in: a sign-out or another account
 * returns every cell to `loading` (unanswered) or idle and drops any answer
 * still in flight.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { Licence } from "@momo/types";
import { AccessRequest } from "@revia/types";
import type {
	ConnectClient,
	GitHubConnection,
	LandingViews,
	Licence as LicenceRecord,
	SignLicence,
} from "@revia/types";
import { Subject } from "rxjs";

/** A list read: unanswered, refused or failed, or answered (possibly empty). */
export type ListRead<T> =
	| { status: "loading" }
	| { status: "error" }
	| { status: "ready"; items: T[] };

export type AccessRequestRow = AccessRequest.AccessRequest & { id: string };
export type LicenceRow = LicenceRecord.Licence & { id: string };

export const accessRequests = state<ListRead<AccessRequestRow>>(
	"revia-backoffice/access-requests",
).memory({ status: "loading" });
export const licences = state<ListRead<LicenceRow>>(
	"revia-backoffice/licences",
).memory({ status: "loading" });

/** What a press asks of a request: the status it moves to. */
export type DecisionAct = AccessRequest.AccessRequestStatus;

/** A press per request: in flight, answered but not yet seen by the
 *  listener, or refused — retryable, denied to this account, the request
 *  gone, or already granted. */
export type Decision = {
	act: DecisionAct;
	state: "busy" | "done" | "retry" | "denied" | "missing" | "settled";
};
export const decisions = state<Record<string, Decision>>(
	"revia-backoffice/decisions",
).memory({});

/** Approve under a name, set aside for later, or return to waiting: the
 *  request and the status it moves to. */
export const decide$ = new Subject<
	{ id: string; act: "granted"; name: string } | { id: string; act: "pending" | "later" }
>();

/** The name staff will found each request's organization under, as edited
 *  here; a request absent from it is founded under the name it carries. */
export const orgNames = state<Record<string, string>>(
	"revia-backoffice/org-names",
).memory({});

/** Which requests the list shows: those waiting, set aside, or granted. */
export const requestsFilter = state<AccessRequest.AccessRequestStatus>(
	"revia-backoffice/requests-filter",
).memory("pending");
/** Show another status; a request decided here stays in view until then. */
export const chooseRequestsFilter$ = new Subject<AccessRequest.AccessRequestStatus>();

/** Re-issue every list read after a failure. */
export const reloadLists$ = new Subject<void>();

/** A licence's shown state, as the deployment itself derives it
 *  (`Licence.deriveLicenceState`), and the moment it turns read-only. */
export function licenceStanding(
	licence: Licence.LicencePayload,
	projectId: string,
	now: number,
) {
	return {
		state: Licence.deriveLicenceState(licence, new Date(now), {
			licenceRequired: true,
			projectId,
		}),
		readOnlyFrom: readOnlyFrom(new Date(licence.endsAt), licence.graceDays),
		// The last instant still in grace: "grace until" names this day.
		graceUntil: new Date(readOnlyFrom(new Date(licence.endsAt), licence.graceDays).getTime() - 1),
	};
}

/** The first read-only instant: just past the end plus the grace days, as
 *  `deriveLicenceState` counts them. */
export const readOnlyFrom = (endsAt: Date, graceDays: number) =>
	new Date(endsAt.getTime() + graceDays * 86_400_000 + 1);

/** Newest first, in the chosen status, plus any request decided here: a
 *  row the person just moved stays where they pressed until they pick
 *  another status. */
export const requestsView = computed(() => {
	const read = accessRequests.value;
	if (read.status !== "ready") return read;
	const marks = decisions.value;
	const filter = requestsFilter.value;
	return {
		status: "ready" as const,
		total: read.items.length,
		counts: {
			pending: read.items.filter((r) => r.status === "pending").length,
			later: read.items.filter((r) => r.status === "later").length,
			granted: read.items.filter((r) => r.status === "granted").length,
		},
		rows: [...read.items]
			.filter((r) => r.status === filter || marks[r.id] !== undefined)
			.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())
			.map((r) => {
				const mark = marks[r.id];
				// What approving founds the organization as: the edit, else the
				// requested name.
				const foundAs = orgNames.value[r.id] ?? r.organization;
				return {
					id: r.id,
					status: r.status,
					organization: r.organization,
					foundAs,
					foundAsValid: AccessRequest.AccessRequestOrganizationSchema.safeParse(foundAs).success,
					// A granted request keeps neither: the founder is reached by email.
					businessType: r.status === "granted" ? null : r.businessType,
					phone: r.status === "granted" ? null : r.phone,
					email: r.email,
					locale: r.locale,
					createdAt: r.createdAt.toDate(),
					expiresAt: r.status === "granted" ? null : r.expiresAt.toDate(),
					reviewedAt: r.reviewedAt ? r.reviewedAt.toDate() : null,
					// Only a mail actually sent is said to have gone out.
					grantMailed: r.status === "granted" && r.grantMailedAt != null,
					// A press still settling: in flight, or answered but the listener
					// has not yet shown the request in its new status.
					pressing:
						mark && (mark.state === "busy" || (mark.state === "done" && r.status !== mark.act))
							? mark.act
							: null,
					refused:
						mark && mark.state !== "busy" && mark.state !== "done"
							? { act: mark.act, why: mark.state }
							: null,
				};
			}),
	};
});

/** Each managed client's licence, newest signed first, with its shown state. */
export const licencesView = computed(() => {
	const read = licences.value;
	if (read.status !== "ready") return read;
	const now = Date.now();
	return {
		status: "ready" as const,
		rows: [...read.items]
			.sort((a, b) => b.signedAt.toMillis() - a.signedAt.toMillis())
			.map((l) => ({
				id: l.id,
				client: l.client,
				environment: l.environment,
				token: l.token,
				signedAt: l.signedAt.toDate(),
				endsAt: new Date(l.endsAt),
				...licenceStanding(l, l.id, now),
			})),
	};
});

/** Why a signing was refused: the end has passed, the client's name gives no
 *  environment name, Revia's signing is not set up yet, or this account may
 *  not. */
export type SignRefusal = "ended" | "unusable-name" | "not-set-up" | "denied";

/** Signing a licence: in flight; refused; failed, retryably or not; or
 *  signed, with the token and the environment it belongs in. */
export type SignAct =
	| { status: "idle" }
	| { status: "busy" }
	| { status: "failed"; retryable: boolean }
	| { status: "refused"; why: SignRefusal }
	| { status: "signed"; token: string; environment: string };
export const signAct = state<SignAct>("revia-backoffice/sign-act").memory({ status: "idle" });
/** Sign the licence. */
export const signLicence$ = new Subject<SignLicence.SignLicenceInput>();
/** The sign form closed: its act is let go. */
export const closeSign$ = new Subject<void>();

/** Whether Revia's GitHub App can act on Revent: unanswered, unreadable, or
 *  the server's answer. */
export const gitHub = state<
	{ status: "loading" } | { status: "error" } | { status: "ready"; connection: GitHubConnection.GitHubConnectionOutput }
>("revia-backoffice/github").memory({ status: "loading" });
/** Ask again: after the App was registered or installed in another tab. */
export const recheckGitHub$ = new Subject<void>();

/** Connecting a client, or renewing its licence, through the App: in flight;
 *  answered with each step's outcome; refused before anything was signed —
 *  the App not connected or not installed, or a signing refusal; or failed,
 *  retryably or not. */
export type ConnectAct =
	| { status: "idle" }
	| { status: "busy" }
	| { status: "answered"; out: ConnectClient.ConnectClientOutput }
	| { status: "refused"; why: "not-connected" | "not-installed" | SignRefusal }
	| { status: "failed"; retryable: boolean };
export const connectAct = state<ConnectAct>("revia-backoffice/connect-act").memory({ status: "idle" });
/** Connect or renew. */
export const connectClient$ = new Subject<ConnectClient.ConnectClientInput>();
/** The connecting dialog closed: its act is let go. */
export const closeConnect$ = new Subject<void>();

/** The visit report's span: the last 7 or 30 UTC days, today included. */
export type VisitRange = "7d" | "30d";
export const visitRange = state<VisitRange>("revia-backoffice/visit-range").memory("7d");
/** Show another span; the report starts unanswered again. */
export const chooseVisitRange$ = new Subject<VisitRange>();

/** One UTC day's counts, keyed by its day. */
export type LandingDay = LandingViews.LandingViews & { day: string };
/** The report's read: unanswered, failed, or the days it answered for, with
 *  the span it was asked for so a late answer for another span is plain. */
export const landingViews = state<
	| { status: "loading" }
	| { status: "error" }
	| { status: "ready"; from: string; to: string; items: LandingDay[] }
>("revia-backoffice/landing-views").memory({ status: "loading" });

/** The first and last UTC day (`yyyy-mm-dd`) a span covers, ending today. */
export const visitSpan = (range: VisitRange, now: number) => ({
	from: new Date(now - ((range === "7d" ? 7 : 30) - 1) * 86_400_000).toISOString().slice(0, 10),
	to: new Date(now).toISOString().slice(0, 10),
});

/** Rows ranked by count, largest first, the past-the-cap bucket last. */
const ranked = (counts: Map<string, number>, other: number) => ({
	rows: [...counts].sort((a, b) => b[1] - a[1]).map(([label, views]) => ({ label, views })),
	other,
});

/** The visit report: page views over the span, per day (a day without a
 *  document counted as none), and by referrer and campaign source. */
export const visitReport = computed(() => {
	const read = landingViews.value;
	if (read.status !== "ready") return read;
	const byDay = new Map(read.items.map((d) => [d.day, d]));
	const days = [];
	for (let t = Date.parse(`${read.from}T00:00:00Z`); t <= Date.parse(`${read.to}T00:00:00Z`); t += 86_400_000) {
		const day = new Date(t).toISOString().slice(0, 10);
		days.push({ day, views: Object.values(byDay.get(day)?.paths ?? {}).reduce((sum, n) => sum + n, 0) });
	}
	const referrers = new Map<string, number>();
	const sources = new Map<string, number>();
	for (const d of read.items) {
		for (const [origin, n] of Object.entries(d.referrers ?? {})) {
			const host = new URL(origin).host;
			referrers.set(host, (referrers.get(host) ?? 0) + n);
		}
		for (const [source, n] of Object.entries(d.utmSources ?? {})) sources.set(source, (sources.get(source) ?? 0) + n);
	}
	const total = days.reduce((sum, d) => sum + d.views, 0);
	const referrersOther = read.items.reduce((sum, d) => sum + (d.referrersOther ?? 0), 0);
	const referred = [...referrers.values()].reduce((sum, n) => sum + n, 0) + referrersOther;
	return {
		status: "ready" as const,
		from: read.from,
		to: read.to,
		total,
		days,
		referrers: ranked(referrers, referrersOther),
		// A direct arrival is counted in the day's paths alone.
		unreferred: Math.max(0, total - referred),
		sources: ranked(sources, read.items.reduce((sum, d) => sum + (d.utmSourcesOther ?? 0), 0)),
	};
});
